import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Plus, Trash2, Send, Mail, RefreshCw, Printer, Copy, ChevronDown, ExternalLink } from "lucide-react";

const PRICE_ROWS = [
  ["A4", "80"], ["A4", "250"], ["A3", "80"], ["A3", "250"],
];

export default function AdminSettings() {
  return (
    <AdminLayout>
      <div className="p-6 md:p-10 max-w-4xl">
        <span className="uppercase text-xs tracking-[0.2em] font-medium text-brand">Configuration</span>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink mt-2 mb-8">Settings</h1>
        <Tabs defaultValue="pricing">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">Email (SMTP2GO)</TabsTrigger>
            <TabsTrigger value="m365" data-testid="tab-m365">Microsoft 365</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="printer" data-testid="tab-printer">Thermal Printer</TabsTrigger>
          </TabsList>
          <TabsContent value="pricing"><PricingTab /></TabsContent>
          <TabsContent value="email"><EmailTab /></TabsContent>
          <TabsContent value="m365"><M365Tab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="printer"><PrinterTab /></TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

function Card({ title, desc, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8">
      <h2 className="font-heading font-bold text-xl text-ink">{title}</h2>
      {desc && <p className="text-sm text-slate-500 mt-1 mb-6">{desc}</p>}
      {children}
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block uppercase text-xs tracking-[0.15em] font-medium text-slate-500 mb-2">{label}</label>
      <input {...props} className="w-full px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
    </div>
  );
}

function SaveBtn({ loading, children = "Save changes", ...p }) {
  return (
    <button {...p} disabled={loading} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">
      {loading && <Loader2 className="animate-spin" size={16} />} {children}
    </button>
  );
}

function PricingTab() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/pricing").then(({ data }) => setP(data)); }, []);
  if (!p) return <Loader />;
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const values = { ...p };
      ["double_sided_multiplier", ...PRICE_ROWS.flatMap(([s, g]) => [`price_${s.toLowerCase()}_${g}_bw`, `price_${s.toLowerCase()}_${g}_color`])]
        .forEach((k) => (values[k] = parseFloat(values[k]) || 0));
      const { data } = await api.put("/admin/settings/pricing", { values });
      setP(data); toast.success("Pricing saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Card title="Pricing" desc="Set the price per copy for each combination of size, paper and colour. Double-sided applies a multiplier.">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uppercase text-xs tracking-[0.12em] text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-4 font-medium">Size</th><th className="py-2 pr-4 font-medium">Paper</th>
              <th className="py-2 pr-4 font-medium">Mono (€)</th><th className="py-2 font-medium">Colour (€)</th>
            </tr>
          </thead>
          <tbody>
            {PRICE_ROWS.map(([s, g]) => {
              const bw = `price_${s.toLowerCase()}_${g}_bw`, col = `price_${s.toLowerCase()}_${g}_color`;
              return (
                <tr key={s + g} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-semibold text-ink">{s}</td>
                  <td className="py-3 pr-4 text-slate-600">{g}gsm</td>
                  <td className="py-3 pr-4"><input data-testid={`price-${bw}`} type="number" step="0.01" value={p[bw]} onChange={(e) => set(bw, e.target.value)} className="w-24 px-3 py-1.5 rounded-md border-2 border-slate-200 focus:border-brand focus:outline-none" /></td>
                  <td className="py-3"><input data-testid={`price-${col}`} type="number" step="0.01" value={p[col]} onChange={(e) => set(col, e.target.value)} className="w-24 px-3 py-1.5 rounded-md border-2 border-slate-200 focus:border-brand focus:outline-none" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-6 max-w-xs">
        <Field label="Double-sided multiplier" type="number" step="0.1" value={p.double_sided_multiplier} onChange={(e) => set("double_sided_multiplier", e.target.value)} data-testid="price-multiplier" />
      </div>
      <div className="mt-6"><SaveBtn loading={saving} onClick={save} data-testid="save-pricing" /></div>
    </Card>
  );
}

function EmailTab() {
  const [s, setS] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.get("/admin/settings/smtp").then(({ data }) => { setS(data); setTestTo(data.sender || ""); }); }, []);
  if (!s) return <Loader />;
  const save = async () => {
    setSaving(true);
    try {
      const body = { sender: s.sender, sender_name: s.sender_name };
      if (apiKey) body.api_key = apiKey;
      const { data } = await api.put("/admin/settings/smtp", body);
      setS(data); setApiKey(""); toast.success("SMTP settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try { await api.post("/admin/settings/smtp/test", { email: testTo }); toast.success("Test email sent!"); }
    catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };
  return (
    <Card title="SMTP2GO email" desc="Credentials used to send magic links, confirmations and notifications. The API key is stored encrypted and never shown again.">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Sender email (verified in SMTP2GO)" type="email" value={s.sender} onChange={(e) => setS({ ...s, sender: e.target.value })} data-testid="smtp-sender" placeholder="no-reply@borrowageek.com" />
          <Field label="Sender name" value={s.sender_name} onChange={(e) => setS({ ...s, sender_name: e.target.value })} data-testid="smtp-sender-name" />
        </div>
        <Field label="SMTP2GO API key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid="smtp-api-key" placeholder={s.configured ? `Current: ${s.masked_key} — leave blank to keep` : "api-xxxxxxxx"} autoComplete="off" />
        <div className="flex items-center gap-3">
          <SaveBtn loading={saving} onClick={save} data-testid="save-smtp" />
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.configured ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
            {s.configured ? "Configured" : "Not configured"}
          </span>
        </div>
      </div>
      <div className="mt-8 pt-6 border-t border-slate-200">
        <label className="block uppercase text-xs tracking-[0.15em] font-medium text-slate-500 mb-2">Send a test email</label>
        <div className="flex gap-3">
          <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="smtp-test-to" className="flex-1 px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none" placeholder="recipient@example.com" />
          <button onClick={test} disabled={testing} data-testid="send-test-email" className="inline-flex items-center gap-2 border-2 border-ink text-ink font-semibold px-4 py-2.5 rounded-lg hover:bg-ink hover:text-white transition-colors disabled:opacity-60">
            {testing ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send test
          </button>
        </div>
      </div>
    </Card>
  );
}

function M365SetupGuide({ mailbox }) {
  const [open, setOpen] = useState(false);
  const steps = [
    { t: "Open the Azure Portal", d: <>Sign in at <b>portal.azure.com</b> with a Microsoft 365 <b>Global Administrator</b> account, then go to <b>Microsoft Entra ID</b> (formerly Azure Active Directory).</> },
    { t: "Register a new application", d: <>Go to <b>App registrations → New registration</b>. Name it e.g. <i>"Borrow A Geek Print Inbox"</i>. Under "Supported account types" choose <b>Accounts in this organizational directory only</b>. Leave Redirect URI blank and click <b>Register</b>.</> },
    { t: "Copy the Tenant ID and Client ID", d: <>On the app's <b>Overview</b> page copy the <b>Application (client) ID</b> and the <b>Directory (tenant) ID</b> — paste these into the fields below.</> },
    { t: "Create a client secret", d: <>Go to <b>Certificates & secrets → New client secret</b>. Add a description, pick an expiry (e.g. 24 months), click <b>Add</b>, then <b>immediately copy the secret Value</b> (not the Secret ID) — it's only shown once. Paste it into "Client secret" below.</> },
    { t: "Add application (app-only) permissions", d: <>Go to <b>API permissions → Add a permission → Microsoft Graph → Application permissions</b>. Add both <b>Mail.Read</b> and <b>Mail.ReadWrite</b>. (Application, not Delegated.)</> },
    { t: "Grant admin consent", d: <>Back on the API permissions screen, click <b>Grant admin consent for [your org]</b> and confirm. Both permissions should show a green "Granted" tick.</> },
    { t: "(Recommended) Restrict access to one mailbox", d: <>By default an app-only app can read <i>every</i> mailbox. To limit it to just <b>{mailbox || "your print mailbox"}</b>, run this in <b>Exchange Online PowerShell</b>:
      <pre className="font-mono text-[11px] bg-ink text-white rounded-md p-3 mt-2 overflow-x-auto whitespace-pre-wrap">{`New-ApplicationAccessPolicy \`
  -AppId <CLIENT_ID> \`
  -PolicyScopeGroupId ${mailbox || "print@borrowageek.com"} \`
  -AccessRight RestrictAccess \`
  -Description "BAG print inbox only"`}</pre></> },
    { t: "Fill in the fields below & save", d: <>Enter the <b>Tenant ID</b>, <b>Client ID</b>, the <b>mailbox address</b> that receives print requests, and the <b>client secret</b>. Click <b>Save</b>, then <b>Check inbox now</b> to test. Unread emails become "Awaiting details" jobs and the sender is emailed a questionnaire link.</> },
  ];
  return (
    <Card title="How to set up Microsoft 365 (step by step)" desc="One-time setup in your Microsoft 365 / Azure admin. You'll need Global Administrator access.">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="m365-guide-toggle"
        className="flex items-center gap-2 text-sm font-semibold text-brand hover:underline">
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        {open ? "Hide instructions" : "Show step-by-step instructions"}
      </button>
      {open && (
        <ol className="mt-5 space-y-4" data-testid="m365-guide-steps">
          {steps.map((st, i) => (
            <li key={i} className="flex gap-3">
              <span className="grid place-items-center w-6 h-6 rounded-full bg-ink text-white text-xs font-bold shrink-0">{i + 1}</span>
              <div className="text-sm text-slate-600 leading-relaxed">
                <div className="font-semibold text-ink">{st.t}</div>
                <div className="mt-0.5">{st.d}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
      {open && (
        <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-5 border-2 border-ink text-ink font-semibold px-4 py-2.5 rounded-lg hover:bg-ink hover:text-white transition-colors text-sm" data-testid="open-azure">
          Open Azure App registrations <ExternalLink size={15} />
        </a>
      )}
    </Card>
  );
}

function M365Tab() {
  const [s, setS] = useState(null);
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [polling, setPolling] = useState(false);
  const [simEmail, setSimEmail] = useState("");
  const [simming, setSimming] = useState(false);
  useEffect(() => { api.get("/admin/settings/m365").then(({ data }) => setS(data)); }, []);
  if (!s) return <Loader />;
  const save = async () => {
    setSaving(true);
    try {
      const body = { tenant_id: s.tenant_id, client_id: s.client_id, mailbox: s.mailbox };
      if (secret) body.client_secret = secret;
      const { data } = await api.put("/admin/settings/m365", body);
      setS({ ...s, configured: data.configured, has_secret: data.configured }); setSecret(""); toast.success("Microsoft 365 settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const poll = async () => {
    setPolling(true);
    try { const { data } = await api.post("/admin/m365/poll"); toast.success(`Checked inbox: ${data.processed} messages, ${data.created} new jobs`); }
    catch (e) { toast.error(apiError(e)); } finally { setPolling(false); }
  };
  const simulate = async () => {
    setSimming(true);
    try { await api.post("/admin/simulate-inbound", { email: simEmail }); toast.success("Simulated inbound email — questionnaire link created & emailed"); }
    catch (e) { toast.error(apiError(e)); } finally { setSimming(false); }
  };
  return (
    <div className="space-y-6">
      <M365SetupGuide mailbox={s.mailbox} />
      <Card title="Microsoft 365 inbound" desc="Connect your M365 mailbox (via Azure app registration with Mail.Read + Mail.ReadWrite application permissions) to turn incoming emails into print jobs automatically.">
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Tenant ID (Directory ID)" value={s.tenant_id} onChange={(e) => setS({ ...s, tenant_id: e.target.value })} data-testid="m365-tenant" />
            <Field label="Client (App) ID" value={s.client_id} onChange={(e) => setS({ ...s, client_id: e.target.value })} data-testid="m365-client" />
          </div>
          <Field label="Mailbox email" type="email" value={s.mailbox} onChange={(e) => setS({ ...s, mailbox: e.target.value })} data-testid="m365-mailbox" placeholder="print@borrowageek.com" />
          <Field label="Client secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="m365-secret" placeholder={s.has_secret ? "Saved — leave blank to keep" : "Enter client secret"} autoComplete="off" />
          <div className="flex items-center gap-3">
            <SaveBtn loading={saving} onClick={save} data-testid="save-m365" />
            <button onClick={poll} disabled={polling || !s.configured} data-testid="m365-poll" className="inline-flex items-center gap-2 border-2 border-ink text-ink font-semibold px-4 py-2.5 rounded-lg hover:bg-ink hover:text-white transition-colors disabled:opacity-40">
              {polling ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Check inbox now
            </button>
          </div>
        </div>
      </Card>
      <Card title="Demo: simulate an inbound email" desc="No live mailbox yet? Simulate a customer emailing a file. The system creates an 'awaiting details' job and emails a questionnaire link.">
        <div className="flex gap-3">
          <input type="email" value={simEmail} onChange={(e) => setSimEmail(e.target.value)} data-testid="sim-email" className="flex-1 px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none" placeholder="customer@example.com" />
          <button onClick={simulate} disabled={simming || !simEmail} data-testid="simulate-inbound" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60">
            {simming ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Simulate
          </button>
        </div>
      </Card>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "staff", password: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data.users));
  useEffect(() => { load(); }, []);
  if (!users) return <Loader />;
  const add = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/admin/users", form); toast.success("User added"); setForm({ email: "", name: "", role: "staff", password: "" }); load(); }
    catch (er) { toast.error(apiError(er)); } finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("User removed"); load(); }
    catch (er) { toast.error(apiError(er)); }
  };
  return (
    <div className="space-y-6">
      <Card title="Add a user" desc="Staff & admin accounts sign in with email + password. Customers are created automatically when they submit or sign in.">
        <form onSubmit={add} className="grid sm:grid-cols-2 gap-4">
          <Field label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
          <Field label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
          <div>
            <label className="block uppercase text-xs tracking-[0.15em] font-medium text-slate-500 mb-2">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role" className="w-full px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none bg-white">
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
              <option value="customer">Customer</option>
            </select>
          </div>
          {form.role !== "customer" && (
            <Field label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" placeholder="min 6 characters" />
          )}
          <div className="sm:col-span-2"><SaveBtn loading={saving} data-testid="add-user"><Plus size={16} /> Add user</SaveBtn></div>
        </form>
      </Card>
      <Card title="All users">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left uppercase text-xs tracking-[0.12em] text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 font-medium">Name</th><th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Role</th><th className="py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-semibold text-ink">{u.name}</td>
                  <td className="py-3 pr-4 text-slate-600">{u.email}</td>
                  <td className="py-3 pr-4"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{u.role}</span></td>
                  <td className="py-3 text-right">
                    <button onClick={() => del(u.id)} data-testid={`delete-user-${u.email}`} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PrinterTab() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [busy, setBusy] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL;
  const load = () => api.get("/admin/settings/printer").then(({ data }) => setP(data));
  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.get("/admin/settings/printer").then(({ data }) => setP((cur) => (cur ? { ...cur, agent: data.agent } : data))).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, []);
  if (!p) return <Loader />;
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings/printer", { enabled: p.enabled, ip_address: p.ip_address, port: parseInt(p.port, 10) || 9100, beep: p.beep }); setP(data); toast.success("Printer settings saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const genToken = async () => {
    setBusy("token");
    try { const { data } = await api.post("/admin/settings/printer/token"); setNewToken(data.token); await load(); toast.success("New agent token generated"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const testPrint = async () => {
    setBusy("test");
    try { const { data } = await api.post("/admin/settings/printer/test"); toast.success(data.message || "Test ticket queued"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied to clipboard"); };

  return (
    <div className="space-y-6">
      <Card title="Networked thermal printer" desc="Capture CA-PP-10000B. A small print agent runs on your shop network and prints a ticket (with a beep) for each new job. The agent only makes outbound calls — no firewall holes needed.">
        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 mb-6">
          <div>
            <div className="font-semibold text-ink">Enable printer alerts</div>
            <div className="text-xs text-slate-500">Print a ticket &amp; beep for each new job</div>
          </div>
          <button onClick={() => setP({ ...p, enabled: !p.enabled })} data-testid="printer-enabled" className={`w-12 h-7 rounded-full transition-colors relative ${p.enabled ? "bg-brand" : "bg-slate-300"}`}>
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${p.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Printer IP address" value={p.ip_address} onChange={(e) => setP({ ...p, ip_address: e.target.value })} data-testid="printer-ip" placeholder="192.168.1.50" />
          <Field label="Port" type="number" value={p.port} onChange={(e) => setP({ ...p, port: e.target.value })} data-testid="printer-port" />
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm font-medium text-slate-700">
          <input type="checkbox" checked={p.beep} onChange={(e) => setP({ ...p, beep: e.target.checked })} data-testid="printer-beep" className="w-4 h-4 accent-brand" />
          Sound a beep on new jobs
        </label>
        <div className="mt-6"><SaveBtn loading={saving} onClick={save} data-testid="save-printer" /></div>
      </Card>

      <Card title="Print agent connection" desc="Generate a token, then run the print agent on a shop computer with this token. Keep the token secret.">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${p.has_agent_token ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
            {p.has_agent_token ? `Token active •••• ${p.token_last4}` : "No token yet"}
          </span>
          <button onClick={genToken} disabled={busy === "token"} data-testid="generate-token" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60">
            {busy === "token" ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} {p.has_agent_token ? "Regenerate token" : "Generate agent token"}
          </button>
          <button onClick={testPrint} disabled={busy === "test" || !p.has_agent_token} data-testid="test-print" className="inline-flex items-center gap-2 border-2 border-ink text-ink font-semibold px-4 py-2.5 rounded-lg hover:bg-ink hover:text-white transition-colors disabled:opacity-40">
            {busy === "test" ? <Loader2 className="animate-spin" size={16} /> : <Printer size={16} />} Send test print
          </button>
        </div>
        <AgentStatus a={p.agent} />
        {newToken && (
          <div className="mt-4 rounded-lg border-2 border-brand bg-orange-50 p-4" data-testid="new-token-box">
            <div className="text-xs font-semibold uppercase tracking-wide text-brand mb-1">Copy this token now — it won't be shown again</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-sm text-ink break-all bg-white rounded px-3 py-2 border border-orange-200">{newToken}</code>
              <button onClick={() => copy(newToken)} className="p-2 rounded-md border border-slate-200 hover:border-brand text-slate-600 hover:text-brand" title="Copy"><Copy size={16} /></button>
            </div>
          </div>
        )}
        <div className="mt-6"><WindowsAgentGuide backend={backend} /></div>
      </Card>
    </div>
  );
}

const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(iso).toLocaleString();
};

function AgentStatus({ a }) {
  if (!a) return null;
  const errorIsCurrent = a.last_error && (!a.last_print_at || a.last_error_at > a.last_print_at);
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-sm" data-testid="agent-status">
      <div className="flex flex-wrap items-center gap-3">
        <span data-testid="agent-online-badge" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${a.online ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
          <span className={`w-2 h-2 rounded-full ${a.online ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
          {a.online ? "Agent online" : "Agent offline"}
        </span>
        <span className="text-xs text-slate-500 break-all" data-testid="agent-last-seen">Last seen {ago(a.last_seen)}{a.host ? ` · ${a.host}` : ""}{a.version ? ` · v${a.version}` : ""}</span>
        <span className="text-xs text-slate-500" data-testid="agent-last-print">Last print {ago(a.last_print_at)}</span>
      </div>
      {a.instances?.length > 1 && (
        <p className="mt-3 text-xs font-semibold text-amber-700 bg-amber-50 rounded-md px-3 py-2" data-testid="agent-multiple-warning">
          {a.instances.length} agent copies are running ({a.instances.map((i) => i.host || "unknown").join(", ")}). Re-run the latest installer so only one copy runs.
        </p>
      )}
      {a.online && a.version && a.version.startsWith("1") && (
        <p className="mt-3 text-xs text-amber-700" data-testid="agent-outdated-warning">This agent is an old version. Download the new files below and re-run the installer.</p>
      )}
      {errorIsCurrent && (
        <p className="mt-3 text-xs text-red-700 bg-red-50 rounded-md px-3 py-2" data-testid="agent-last-error">
          <b>Last printer error ({ago(a.last_error_at)}):</b> {a.last_error}
        </p>
      )}
      {!a.online && (
        <p className="mt-3 text-xs text-slate-500">No agent has checked in for over a minute. Check the shop PC is on and logged in, then run <code className="font-mono">diagnose-windows.bat</code>.</p>
      )}
    </div>
  );
}

function WindowsAgentGuide({ backend }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="windows-agent-guide">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="windows-guide-toggle"
        className="flex items-center gap-2 text-sm font-semibold text-ink">
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        Install the print agent on Windows (step by step)
      </button>
      {open && (
        <div className="mt-4 text-sm text-slate-600 space-y-4">
          <p className="text-xs text-slate-500">Do this on a Windows PC at the shop that stays on during opening hours and is on the same network as the printer. It only makes outbound calls — no firewall changes needed.</p>

          <Step n="1" t="Install Python">
            Download Python 3.9+ from <b>python.org/downloads</b>. Run the installer and <b>tick “Add python.exe to PATH”</b> before clicking Install. Either “Install for all users” or the default “Install for me only” works.
          </Step>
          <Step n="2" t="Get the agent files">
            Download the files below into a new folder, e.g. <code className="font-mono">C:\BAGPrintAgent</code>:
            <div className="flex flex-wrap gap-2 mt-2">
              <a href={`${backend}/print-agent/agent.py`} download className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand border border-orange-200 bg-orange-50 rounded-md px-2.5 py-1.5 hover:bg-orange-100" data-testid="dl-agent-py">agent.py</a>
              <a href={`${backend}/print-agent/install-windows.bat`} download className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand border border-orange-200 bg-orange-50 rounded-md px-2.5 py-1.5 hover:bg-orange-100" data-testid="dl-install-bat">install-windows.bat</a>
              <a href={`${backend}/print-agent/diagnose-windows.bat`} download className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand border border-orange-200 bg-orange-50 rounded-md px-2.5 py-1.5 hover:bg-orange-100" data-testid="dl-diagnose-bat">diagnose-windows.bat</a>
              <a href={`${backend}/print-agent/README.md`} download className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 bg-white rounded-md px-2.5 py-1.5 hover:border-brand" data-testid="dl-readme">README.md</a>
            </div>
          </Step>
          <Step n="3" t="Generate your agent token">
            Scroll up and click <b>Generate agent token</b>, then copy the token (shown once).
          </Step>
          <Step n="4" t="Edit the installer">
            Right-click <code className="font-mono">install-windows.bat</code> → <b>Edit</b>. Set the two lines at the top:
            <pre className="font-mono text-[11px] bg-ink text-white rounded-md p-3 mt-2 overflow-x-auto whitespace-pre-wrap">{`set "BACKEND_URL=${backend}"
set "AGENT_TOKEN=paste-your-token-here"`}</pre>
            Save and close.
          </Step>
          <Step n="5" t="Run the installer as Administrator">
            Right-click <code className="font-mono">install-windows.bat</code> → <b>Run as administrator</b>. It installs the <code className="font-mono">requests</code> library and creates a scheduled task called <b>BAGPrintAgent</b> that runs the agent as your user, starts it now and at every logon, restarts it if it crashes, and relaunches it within 5 minutes if it ever stops. It also removes any old agent service or duplicate copies, then checks the printer connection.
          </Step>
          <Step n="6" t="Enter the printer IP & test">
            Fill in the <b>Printer IP address</b> and <b>Port</b> above and click <b>Save</b>. Then click <b>Send test print</b> — a ticket should print within a few seconds.
          </Step>

          <div className="pt-2">
            <div className="font-semibold text-ink text-xs uppercase tracking-wide mb-1">Prefer to run it manually (no task)?</div>
            <p className="text-xs text-slate-500 mb-2">Open <b>Command Prompt</b> in the folder and run:</p>
            <pre className="font-mono text-[11px] bg-ink text-white rounded-md p-3 overflow-x-auto whitespace-pre-wrap">{`pip install requests
set BAG_BACKEND_URL=${backend}
set BAG_AGENT_TOKEN=paste-your-token-here
python agent.py`}</pre>
          </div>

          <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-md p-3">
            <b>Managing the agent:</b> logs are written to <code className="font-mono">agent.log</code> in the folder.
            Start now: <code className="font-mono">schtasks /Run /TN BAGPrintAgent</code> · Stop: <code className="font-mono">schtasks /End /TN BAGPrintAgent</code> · Remove: <code className="font-mono">schtasks /Delete /TN BAGPrintAgent /F</code>.
            If you see “Invalid agent token”, regenerate the token above and re-run the installer. If printing fails, double-click <code className="font-mono">diagnose-windows.bat</code> — it tests the server and the printer connection and tells you what is wrong.
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ n, t, children }) {
  return (
    <div className="flex gap-3">
      <span className="grid place-items-center w-6 h-6 rounded-full bg-ink text-white text-xs font-bold shrink-0">{n}</span>
      <div className="leading-relaxed min-w-0 flex-1 break-words">
        <div className="font-semibold text-ink">{t}</div>
        <div className="mt-0.5 min-w-0">{children}</div>
      </div>
    </div>
  );
}

function Loader() {
  return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-brand" size={26} /></div>;
}
